import { Inject, Injectable } from '@angular/core';
import { TealiumAnalyticsEvent, TealiumAnalyticsService } from '@ddt-web/analytics';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { FundStatsConfigKey, InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { AppStoreConfig } from '@ddt-web/data-service';
import { PasPortfolioPosition, PasPosition } from '@ddt-web/pas';
import { positionDetailsCompareLoaded, positionDetailsLoaded } from '@ddt-web/setup-data-workers';
import { StoreService } from '@ddt-web/store-service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EntityState } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import * as cloneDeep from 'lodash/cloneDeep';
import * as uniqBy from 'lodash/uniqBy';
import { combineLatest, of } from 'rxjs';
import { filter, first, map, switchMap, tap } from 'rxjs/operators';

import { FundStats, FundStatsItem, FundStatsItemHolding, FundStatus } from '../models/fund-stats.interface';
import { StatusColour } from '../models/status-colour.interface';
import { FundStatsUtil } from '../util/fund-stats.util';
import { upsertFundStats, upsertFundStatsCompare } from './fund-stats.actions';


@Injectable()
export class FundStatsEffects {
	@InjectConfig(FundStatsConfigKey.STATUSES) private readonly statuses: FundStatus[];
	@InjectConfig(FundStatsConfigKey.SHOULD_AGGREGATE_FUND_STATS) private readonly shouldAggregateFundStats: boolean = true;
	@InjectConfig(SharedConfigKey.ENABLE_WORST_TAX_OFFENDERS) private readonly isWorstTaxOffendersAofEnabled: boolean = false;

	constructor(
		private actions$: Actions,
		private storeService: StoreService,
		private tealiumAnalyticsService: TealiumAnalyticsService,
		private store: Store,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig
	) { }

	public calculateFundStats$ = createEffect(() =>
		this.actions$.pipe(
			ofType(positionDetailsLoaded),
			map(action => action.payload),
			switchMap((portfolioPositions: PasPortfolioPosition[]) => combineLatest(
				[
					of(portfolioPositions),
					this.isWorstTaxOffendersAofEnabled ? this.storeService.selectByIds(this.storeConfig.taxDetails, portfolioPositions.map(portfolio => portfolio.id)) : of(null)
				]
			)),
			filter(([portfolioPositions, taxDetails]) => !this.isWorstTaxOffendersAofEnabled || !taxDetails.isLoading),
			tap(([portfolioPositions, taxDetails]: [PasPortfolioPosition[], EntityState<PasPortfolioPosition>]) => {
				const portfolioIds = portfolioPositions.map(portfolio => portfolio.id);
				const enrichedPortfolioPositions = portfolioPositions.length && Object.keys(taxDetails?.entities ?? {}).some(id => portfolioIds.includes(id)) ?
					this.insertTaxDetailsFlags(portfolioPositions, taxDetails.entities) :
					portfolioPositions;
				this.store.dispatch(upsertFundStats({ payload: this.buildFundStatsToUpdate(enrichedPortfolioPositions) }));
			})
		), { dispatch: false }
	);

	public calculateFundStatsCompare$ = createEffect(() =>
		this.actions$.pipe(
			ofType(positionDetailsCompareLoaded),
			map(action => action.payload),
			tap((portfolioPositions: any) => {
				this.store.dispatch(upsertFundStatsCompare({ payload: this.buildFundStatsToUpdate(portfolioPositions, true) }));
			})
		), { dispatch: false }
	);

	public trackStatusChanges$ = createEffect(() =>
		this.actions$.pipe(
			ofType(upsertFundStatsCompare),
			map(action => action.payload),
			switchMap((fundStatsCompare: FundStats[]) => {
				return this.storeService.selectById(this.storeConfig.fundStats, 'transient-book')
					.pipe(
						filter((fundStats) => !!fundStats),
						first(),
						tap(fundStats => {
							const upgradedAndDowngradedFunds = FundStatsUtil.countUpgradedAndDowngradedFunds(fundStats['transient-book'], fundStatsCompare[0]);
							this.tealiumAnalyticsService.trackEventTealium(
								new TealiumAnalyticsEvent('iq analysis:fund health stats', {
									fundHealthAnalysisBreakdown: Object.entries(upgradedAndDowngradedFunds)
										.map(([statusChange, count]) => `${statusChange}:${count}`)
										.join('|')
								})
							);
						})
					);

			})
		), { dispatch: false }
	);

	/**
	 * Returns array of all the unique funds analysed (as part of all the analysed portfolios).
	 * The funds are unique based on the requestId - this is the unique identifier used to get the position details from AWA.
	 *
	 * @param positionDetails all the positionDetails from the store
	 * @returns array of all the unique funds analysed
	 */
	private getUniqueFundsInAnalysis(portfolioPositions: PasPortfolioPosition[]): PasPosition[] {
		const aggregatedFunds = portfolioPositions
			?.map(positionDetailsPerPortfolio => positionDetailsPerPortfolio.children ?? []).reduce((acc, value) => acc.concat(value), []);
		return uniqBy(aggregatedFunds, 'requestId');
	}

	private calculateStats(allHoldings: PasPosition[]): FundStats {
		const holdings = allHoldings.filter(holding => holding?.assetType === 'ASSET_TYPE_FUND');
		const redStatus = this.statuses?.find(status => status.statusId === 'red');
		const yellowStatus = this.statuses?.find(status => status.statusId === 'yellow');

		// calculate status stats
		const statusStats = this.calculateStatusStats(holdings);

		// calculate aof stats
		const redAofStats = this.calculateAofStats(holdings, redStatus);
		const yellowAofStats = this.calculateAofStats(holdings, yellowStatus);

		return { status: statusStats, redAof: redAofStats, yellowAof: yellowAofStats };
	}

	private calculateStatusStats(holdings: PasPosition[]): { [key: string]: FundStatsItem } {
		const fundsAndStatuses = holdings.filter(holding => holding.customClientProperties).map(holding => {
			const fundStatus: StatusColour = FundStatsUtil.calculateFundStatus(holding);
			return { holding: holding, isRed: fundStatus === StatusColour.RED, isYellow: fundStatus === StatusColour.YELLOW };
		});

		const red = fundsAndStatuses.filter(fundAndStatus => fundAndStatus.isRed);
		const yellow = fundsAndStatuses.filter(fundAndStatus => fundAndStatus.isYellow);
		const green = fundsAndStatuses.filter(fundAndStatus => !fundAndStatus.isYellow && !fundAndStatus.isRed);

		return {
			red: { count: red.length, holdings: red.map(fundAndStatus => this.holdingToFundStatsItemHolding(fundAndStatus.holding)) },
			yellow: { count: yellow.length, holdings: yellow.map(fundAndStatus => this.holdingToFundStatsItemHolding(fundAndStatus.holding)) },
			green: { count: green.length, holdings: green.map(fundAndStatus => this.holdingToFundStatsItemHolding(fundAndStatus.holding)) }
		};
	}

	private calculateAofStats(positions: PasPosition[], status: FundStatus): { [key: string]: FundStatsItem } {
		const aofStats = {};
		status.aofs.forEach(aof => {
			const holdings = positions
				.filter(holding => aof.subAofIds.some(subAofId => holding.customClientProperties?.[subAofId] === 'true'))
				.map(holding => this.holdingToFundStatsItemHolding(holding));

			aofStats[aof.aofId] = {
				count: holdings.length,
				holdings
			};
		});

		return aofStats;
	}

	private holdingToFundStatsItemHolding(holding: PasPosition): FundStatsItemHolding {
		return {
			requestId: holding.requestId,
			clientId: holding.clientId,
			aladdinId: holding.assetId,
			morningstarSecId: holding.funds?.morningstarSecId?.stringValue
		};
	}

	private buildFundStatsToUpdate(portfolioPositions: { id: string; children: PasPosition[] }[], shouldNotTrackAnalysis: boolean = false): {
		id: string;
		status: { [key: string]: FundStatsItem };
		redAof: { [key: string]: FundStatsItem };
		yellowAof: { [key: string]: FundStatsItem };
	}[] {
		if (this.shouldAggregateFundStats) {
			const uniqueFunds = this.getUniqueFundsInAnalysis(portfolioPositions);
			const fundStats = this.calculateStats(uniqueFunds);

			if (!shouldNotTrackAnalysis) {
				this.trackAnalysis(fundStats);
			}

			return [Object.assign({}, { id: 'transient-book', ...fundStats })];
		} else {
			return portfolioPositions.map(positionDetailsPerPortfolio => {
				const fundStats = this.calculateStats(positionDetailsPerPortfolio?.children ?? []);

				return Object.assign({}, { id: positionDetailsPerPortfolio.id, ...fundStats });
			});
		}
	}

	private trackAnalysis(stats: FundStats): void {
		const analysisStats: string[] = [];
		Object.values(stats).forEach(statsObject => {
			Object.entries(statsObject).forEach(([statsId, statsValue]) => {
				analysisStats.push(`${statsId}:${(<any>statsValue).count}`);
			});
		});

		this.tealiumAnalyticsService.trackEventTealium(
			new TealiumAnalyticsEvent('iq analysis:fund health stats', { fundHealthAnalysisBreakdown: analysisStats.join('|') })
		);
	}

	private insertTaxDetailsFlags(portfolioPositions: PasPortfolioPosition[], taxDetails: { [id: string]: PasPortfolioPosition }): PasPortfolioPosition[] {
		const clonedPositions = cloneDeep(portfolioPositions);
		clonedPositions.forEach((portfolio: PasPortfolioPosition) => {
			const taxPortfolio = taxDetails[portfolio.id];
			portfolio.children.forEach((child: PasPosition) => {
				const taxDetailsChildClients = taxPortfolio?.children.find(taxChild => taxChild.requestId === child.requestId)?.customClientProperties || {};
				if (child.customClientProperties) {
					child.customClientProperties = { ...taxDetailsChildClients, ...child.customClientProperties };
				}
			});
		});

		return clonedPositions;
	}
}
